import type { GetShareSettingsResponse } from '@orbit/api-types'
import { TOGGLEABLE_SHOW_FLAG_KEYS } from './shareFlags'
import React from 'react'
import { SectionDivider } from './EditTripModal.styles'
import { LoadingText, MasterBlock, PublicLinkField, SectionList } from './SharingSection.styles'
import SwitchRow from './SwitchRow'

type SectionFlag = keyof typeof TOGGLEABLE_SHOW_FLAG_KEYS

/**
 * Labels keyed by the toggleable flag map: adding a flag to
 * TOGGLEABLE_SHOW_FLAG_KEYS fails type-check here until a label exists for it, and
 * a label can never be paired with the wrong key.
 */
const SECTION_LABEL: Record<SectionFlag, string> = {
	publicShowItinerary: 'Itinerary',
	publicShowIdeas: 'Ideas',
	publicShowAccommodations: 'Accommodations',
	publicShowFlights: 'Flights',
}

/** Display order comes from the toggleable key map, so UI order cannot drift from it. */
const SECTION_ORDER = Object.keys(TOGGLEABLE_SHOW_FLAG_KEYS) as SectionFlag[]

interface SharingSectionProps {
	/** The Sharing surface is an owner-only decision (ADR 0003); members see nothing. */
	isOwner: boolean
	settings: GetShareSettingsResponse | undefined
	isLoading: boolean
	/** Flags with a PATCH in flight — each locks only its own row (toggles may overlap). */
	savingKeys: ReadonlySet<keyof GetShareSettingsResponse>
	onToggle: (field: keyof GetShareSettingsResponse, value: boolean) => void
	/** The public trip URL the copy button puts on the clipboard. */
	publicUrl: string
}

const SharingSection: React.FC<SharingSectionProps> = ({
	isOwner,
	settings,
	isLoading,
	savingKeys,
	onToggle,
	publicUrl,
}) => {
	if (!isOwner) return null

	if (!settings) {
		return (
			<>
				<SectionDivider />
				<LoadingText>{isLoading ? 'Loading sharing settings…' : 'Sharing settings unavailable.'}</LoadingText>
			</>
		)
	}

	const masterOn = settings.isVisibleToPublic
	const masterSaving = savingKeys.has('isVisibleToPublic')

	return (
		<>
			<SectionDivider />

			<MasterBlock>
				<SwitchRow
					id="share-master"
					title="Share this trip"
					description="Allow anyone with a link to view your trip, no account needed. They will not be able to make any changes."
					checked={masterOn}
					disabled={masterSaving}
					onChange={() => onToggle('isVisibleToPublic', !masterOn)}
				/>

				{/* The link only exists while the master switch is on, so it lives with it. */}
				{masterOn && (
					<PublicLinkField
						caption="Public link"
						value={publicUrl}
						errorMessage="Could not copy the link. Please copy it manually."
					/>
				)}
			</MasterBlock>

			<SectionList>
				{SECTION_ORDER.map(key => {
					const label = SECTION_LABEL[key]
					const checked = settings[key]
					// Section flags are meaningless until the link is live (ADR 0003).
					const disabled = !masterOn || savingKeys.has(key)

					return (
						<SwitchRow
							key={key}
							id={`share-toggle-${key}`}
							title={label}
							checked={checked}
							disabled={disabled}
							onChange={() => onToggle(key, !checked)}
						/>
					)
				})}
			</SectionList>
		</>
	)
}

export default SharingSection
